import { useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent, MouseEvent } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@/components/Button';
import { FormLayout, Larger } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import {
  BankBox,
  RemoveButton,
} from '@/pages/InvoiceList/components/InvoicesMemberModal';
import InvoicesSwiftCodesField from '@/pages/InvoiceList/components/InvoicesSwiftCodesField';
import { FreelancerDetailsResource } from '@/types/resources';
import { api } from '@/utils/api';
import {
  categories,
  countries,
  debtorBanks,
  genders,
  paymentTypes,
  recipientTypes,
} from '@/utils/data/payments';
import { handleFormErrors } from '@/utils/helpers';

export type FormDataT = {
  name?: string;
  date_of_birth?: string;
  gender?: string;
  personal_code?: string;
  personal_email?: string;
  phone_number?: string;
  address: string;
  country: string;
  city: string;
  post_code: string;
  banks: {
    iban: string;
    swift_code: string;
    recipient_type: string;
    debtor_bank: string;
    bank: string;
    bank_country: string;
    payment_type: string;
    bank_address: string;
    note: string;
    true_receiver?: string;
    id?: number;
  }[];
  category?: string;
  work_email?: string;
  vat_number?: string;
  activity_code?: string;
  representative?: string;
  company_address?: string;
};

const InnerFormLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-row-gap: 24px;
  grid-column-gap: 24px;
`;

const AddButtonHolder = styled.div`
  margin-bottom: 24px;
  button {
    width: 100%;
  }
`;

export const BanksBlockHeading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

export const BankBreak = styled.div`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.lightGray};
  height: 1px;
  margin-right: 14px;
  margin-left: 18px;
`;

export const BankNumber = styled.div`
  display: flex;
  height: 24px;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.purpleLighter};
  border-radius: 100px;
  padding: 0 5px;
  min-width: 50px;
`;

const BlockHeading = styled(Text)`
  grid-column: span 2;
`;

interface Props {
  freelancer?: FreelancerDetailsResource;
  onClose: () => void;
}

const FreelancerFormModal: FC<Props> = ({ onClose, freelancer }) => {
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const alerts = useAlerts();

  const generateDefaultValues = (freelancer?: FreelancerDetailsResource) => {
    return {
      name: freelancer?.member.name ?? '',
      address: freelancer?.member.address ?? '',
      post_code: freelancer?.member.post_code ?? '',
      country: freelancer?.member.country ?? '',
      city: freelancer?.member.city ?? '',
      date_of_birth: freelancer?.dateOfBirth ?? '',
      gender: freelancer?.gender ?? '',
      personal_code: freelancer?.personalCode ?? '',
      personal_email: freelancer?.personalEmail ?? '',
      phone_number: freelancer?.phoneNumber ?? '',
      category: freelancer?.category ?? '',
      work_email: freelancer?.workEmail ?? '',
      vat_number: freelancer?.member.vat_number ?? '',
      activity_code: freelancer?.activityCode ?? '',
      representative: freelancer?.member.representative ?? '',
      company_address: freelancer?.member.company_address ?? '',
      banks: freelancer?.member.banks.length
        ? freelancer?.member.banks.map((bank) => {
            return {
              iban: bank.iban,
              swift_code: bank.swift_code,
              recipient_type: bank.recipient_type,
              debtor_bank: bank.debtor_bank,
              bank: bank.bank,
              bank_country: bank.bank_country,
              payment_type: bank.payment_type,
              bank_address: bank.bank_address,
              note: bank.note ?? '',
              true_receiver: bank.true_receiver ?? '',
              id: bank.id,
            };
          })
        : [
            {
              iban: '',
              swift_code: '',
              recipient_type: '',
              debtor_bank: '',
              bank: '',
              bank_country: '',
              payment_type: '',
              bank_address: '',
              note: '',
              true_receiver: '',
            },
          ],
    };
  };

  const {
    handleSubmit,
    setError,
    clearErrors,
    control,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: generateDefaultValues(freelancer),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'banks',
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        if (freelancer) {
          await api.put<FormDataT, FreelancerDetailsResource>(
            `/v1/payments/freelancers/${freelancer.id}`,
            data,
          );
        } else {
          const { id } = await api.post<FormDataT, FreelancerDetailsResource>(
            '/v1/payments/freelancers',
            data,
          );

          navigate(`/general/freelancers/${id}`);
        }

        queryClient.invalidateQueries(['invoices_list'], {
          exact: false,
        });

        queryClient.refetchQueries(['invoices_members'], {
          exact: true,
        });

        queryClient.refetchQueries(['freelancers_details'], {
          exact: false,
        });

        alerts.success(
          `${
            freelancer
              ? 'Freelancer has been succesfully updated'
              : 'New Freelancer has been succesfully added'
          }`,
        );

        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true, false);
      }
    })(e);
  };

  return (
    <Modal stopPropagation={false}>
      <SidePanel
        heading={!!freelancer ? 'Edit freelancer' : 'Add freelancer'}
        onClose={onClose}
      >
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <BlockHeading $type="caption">Personal details</BlockHeading>
              <FormField<FormDataT>
                control={control}
                name="name"
                label="Name Surname"
                placeholder="Add Name"
              />
              <FormField<FormDataT>
                control={control}
                name="date_of_birth"
                label="Date of Birth"
                placeholder="Add Date"
                type="datepicker"
                datepickerType="date"
              />
              <FormField<FormDataT>
                control={control}
                name="gender"
                label="Gender"
                type="select"
                placeholder="Select gender"
                options={genders}
              />
              <FormField<FormDataT>
                control={control}
                name="personal_code"
                label="Personal Code"
                placeholder="Add Code"
              />
              <FormField<FormDataT>
                control={control}
                name="vat_number"
                label="VAT Number"
                placeholder="Add VAT number"
              />
              <FormField<FormDataT>
                control={control}
                name="activity_code"
                label="Activity Code"
                placeholder="Add activity code"
              />
              <FormField<FormDataT>
                control={control}
                name="representative"
                label="Representative"
                placeholder="Add representative"
              />
              <FormField<FormDataT>
                control={control}
                name="company_address"
                label="Company Address"
                placeholder="Add company address"
              />
              <FormField<FormDataT>
                control={control}
                name="personal_email"
                label="Personal Email"
                placeholder="Add Email"
              />
              <FormField<FormDataT>
                control={control}
                name="phone_number"
                label="Phone Number"
                placeholder="Add Number"
              />
              <FormField<FormDataT>
                control={control}
                name="address"
                label="Address"
                placeholder="Add address"
              />
              <FormField<FormDataT>
                control={control}
                name="country"
                label="Country"
                type="select"
                options={countries}
              />
              <FormField<FormDataT>
                control={control}
                name="city"
                label="City"
                placeholder="Add city"
              />
              <FormField<FormDataT>
                control={control}
                name="post_code"
                label="Postcode"
                placeholder="Add postcode"
              />
              <FormField<FormDataT>
                name="category"
                control={control}
                type="select"
                label="Category"
                options={categories}
              />
              <FormField<FormDataT>
                control={control}
                name="work_email"
                label="Work Email"
                placeholder="Add email"
              />

              {!!freelancer && (
                <>
                  {fields.map((_, index) => (
                    <BankBox key={index}>
                      <BanksBlockHeading>
                        <Text $type="caption">Bank details</Text>
                        <BankBreak />
                        <BankNumber>
                          <Text $type="smallBold" $color="white">
                            Nr. {index + 1}
                          </Text>
                        </BankNumber>
                      </BanksBlockHeading>
                      <InnerFormLayout>
                        <FormField<FormDataT>
                          name={`banks.${index}.true_receiver`}
                          control={control}
                          label="True Receiver"
                          placeholder="Add true receiver"
                        />
                        <FormField<FormDataT>
                          name={`banks.${index}.debtor_bank`}
                          control={control}
                          type="select"
                          label="Debtor bank"
                          options={debtorBanks}
                        />
                        <InvoicesSwiftCodesField
                          control={control}
                          name={`banks.${index}.swift_code`}
                          bankFieldName={`banks.${index}.bank`}
                          ibanFieldName={`banks.${index}.iban`}
                          countryFieldName={`banks.${index}.bank_country`}
                          paymentTypeFieldName={`banks.${index}.payment_type`}
                        />
                        <FormField<FormDataT>
                          name={`banks.${index}.recipient_type`}
                          control={control}
                          type="select"
                          label="Recipient type"
                          options={recipientTypes}
                        />
                        <FormField<FormDataT>
                          name={`banks.${index}.payment_type`}
                          control={control}
                          type="select"
                          label="Payment type"
                          options={paymentTypes}
                        />
                        <FormField<FormDataT>
                          name={`banks.${index}.bank_country`}
                          control={control}
                          type="select"
                          label="Bank country"
                          options={countries}
                        />
                        <FormField<FormDataT>
                          name={`banks.${index}.bank_address`}
                          control={control}
                          label="Bank address"
                          placeholder="Add address"
                        />
                        <FormField<FormDataT>
                          name={`banks.${index}.note`}
                          control={control}
                          label="Note"
                          type="textarea"
                          placeholder="Add note"
                        />
                        {index > 0 && (
                          <RemoveButton
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              remove(index);
                            }}
                          >
                            Remove section
                          </RemoveButton>
                        )}
                      </InnerFormLayout>
                    </BankBox>
                  ))}
                  <Larger>
                    <AddButtonHolder>
                      <Button
                        type="button"
                        $type="colorized"
                        onClick={() =>
                          append({
                            iban: '',
                            swift_code: '',
                            recipient_type: '',
                            debtor_bank: '',
                            bank: '',
                            bank_country: '',
                            payment_type: '',
                            bank_address: '',
                            note: '',
                            true_receiver: '',
                          })
                        }
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M8 2V14"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M2 8H14"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Add other bank
                      </Button>
                    </AddButtonHolder>
                  </Larger>
                </>
              )}
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              {!!freelancer ? 'Update' : 'Save'}
            </Button>
            <Button
              color="secondary"
              onClick={(e: MouseEvent) => {
                e.preventDefault();
                onClose();
              }}
            >
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default FreelancerFormModal;
